import { api } from "@/lib/api";
import type {
    Advertiser,
    AdvertiserDashboard,
    AdvertiserEntityType,
    Affiliate,
    AffiliateDashboard,
    AffiliateEntityType,
    AssignmentEntityType,
    AssignmentLogEntry,
    CreateAdvertiserRequest,
    CreateAffiliateRequest,
    ReassignAdvertiserRequest,
    ReassignAffiliateRequest,
    UpdateAdvertiserRequest,
    UpdateAffiliateRequest,
} from "@/types/commerce";

const ADVERTISERS = "/admin/advertisers";
const AFFILIATES = "/admin/affiliates";

// ─── Advertiser CRUD + dashboard + assignment log ──────────────────────────

export interface ListAdvertisersParams {
    includeArchived?: boolean;
}

export async function listAdvertisers(params: ListAdvertisersParams = {}): Promise<Advertiser[]> {
    const q: Record<string, string | number | boolean> = {};
    if (params.includeArchived != null) q.includeArchived = params.includeArchived;
    const { data } = await api.get<Advertiser[]>(ADVERTISERS, { params: q });
    return data;
}

export async function getAdvertiser(id: number): Promise<Advertiser> {
    const { data } = await api.get<Advertiser>(`${ADVERTISERS}/${id}`);
    return data;
}

export async function getAdvertiserBySlug(slug: string): Promise<Advertiser> {
    const { data } = await api.get<Advertiser>(`${ADVERTISERS}/by-slug/${encodeURIComponent(slug)}`);
    return data;
}

export async function getAdvertiserDashboard(id: number): Promise<AdvertiserDashboard> {
    const { data } = await api.get<AdvertiserDashboard>(`${ADVERTISERS}/${id}/dashboard`);
    return data;
}

export async function getAdvertiserAssignmentLog(id: number, limit = 50): Promise<AssignmentLogEntry[]> {
    const { data } = await api.get<AssignmentLogEntry[]>(`${ADVERTISERS}/${id}/assignment-log`, {
        params: { limit },
    });
    return data;
}

export async function createAdvertiser(req: CreateAdvertiserRequest): Promise<Advertiser> {
    const { data } = await api.post<Advertiser>(ADVERTISERS, req);
    return data;
}

export async function updateAdvertiser(id: number, req: UpdateAdvertiserRequest): Promise<Advertiser> {
    const { data } = await api.patch<Advertiser>(`${ADVERTISERS}/${id}`, req);
    return data;
}

/** Soft-delete: flips status to ARCHIVED, keeps FK references intact. */
export async function archiveAdvertiser(id: number): Promise<void> {
    await api.post(`${ADVERTISERS}/${id}/archive`);
}

/** Hard delete. 409 if anything still references this advertiser. */
export async function deleteAdvertiser(id: number): Promise<void> {
    await api.delete(`${ADVERTISERS}/${id}`);
}

// ─── Affiliate CRUD + dashboard + assignment log ───────────────────────────

export interface ListAffiliatesParams {
    includeArchived?: boolean;
}

export async function listAffiliates(params: ListAffiliatesParams = {}): Promise<Affiliate[]> {
    const q: Record<string, string | number | boolean> = {};
    if (params.includeArchived != null) q.includeArchived = params.includeArchived;
    const { data } = await api.get<Affiliate[]>(AFFILIATES, { params: q });
    return data;
}

export async function getAffiliate(id: number): Promise<Affiliate> {
    const { data } = await api.get<Affiliate>(`${AFFILIATES}/${id}`);
    return data;
}

export async function getAffiliateBySlug(slug: string): Promise<Affiliate> {
    const { data } = await api.get<Affiliate>(`${AFFILIATES}/by-slug/${encodeURIComponent(slug)}`);
    return data;
}

export async function getAffiliateDashboard(id: number): Promise<AffiliateDashboard> {
    const { data } = await api.get<AffiliateDashboard>(`${AFFILIATES}/${id}/dashboard`);
    return data;
}

export async function getAffiliateAssignmentLog(id: number, limit = 50): Promise<AssignmentLogEntry[]> {
    const { data } = await api.get<AssignmentLogEntry[]>(`${AFFILIATES}/${id}/assignment-log`, {
        params: { limit },
    });
    return data;
}

export async function createAffiliate(req: CreateAffiliateRequest): Promise<Affiliate> {
    const { data } = await api.post<Affiliate>(AFFILIATES, req);
    return data;
}

export async function updateAffiliate(id: number, req: UpdateAffiliateRequest): Promise<Affiliate> {
    const { data } = await api.patch<Affiliate>(`${AFFILIATES}/${id}`, req);
    return data;
}

/** Soft-delete: flips status to ARCHIVED, keeps FK references intact. */
export async function archiveAffiliate(id: number): Promise<void> {
    await api.post(`${AFFILIATES}/${id}/archive`);
}

/** Hard delete. 409 if anything still references this affiliate. */
export async function deleteAffiliate(id: number): Promise<void> {
    await api.delete(`${AFFILIATES}/${id}`);
}

// ─── Reassignment ───────────────────────────────────────────────────────────
//
// One call site per entity type. The advertiser-owned set (page/page_draft/
// domain/banner) hits `/admin/{type}/{id}/advertiser`; the affiliate-owned
// set (campaign/ad_source) hits `/admin/{type}/{id}/affiliate`. Pass
// `advertiserId=null` (or `affiliateId=null`) to clear the owner. `reason`
// is optional and lands in the matching *_assignment_log table.

/** Map entity-type → URL segment for advertiser routes. */
const ADVERTISER_PATH: Record<AdvertiserEntityType, string> = {
    page: "/admin/pages",
    page_draft: "/admin/constructor/drafts",
    domain: "/admin/domains",
    banner: "/admin/banners",
};

/** Map entity-type → URL segment for affiliate routes. */
const AFFILIATE_PATH: Record<AffiliateEntityType, string> = {
    campaign: "/admin/campaigns",
    ad_source: "/admin/ad-sources",
};

export interface AssignToAdvertiserArgs {
    entityType: AdvertiserEntityType;
    entityId: number;
    advertiserId: number | null;
    reason?: string | null;
}

export async function assignToAdvertiser(args: AssignToAdvertiserArgs): Promise<void> {
    const base = ADVERTISER_PATH[args.entityType];
    if (!base) {
        throw new Error(`Unknown advertiser-owned entity type: ${args.entityType}`);
    }
    const body: ReassignAdvertiserRequest = {
        advertiserId: args.advertiserId,
        reason: args.reason ?? null,
    };
    await api.patch(`${base}/${args.entityId}/advertiser`, body);
}

export interface AssignToAffiliateArgs {
    entityType: AffiliateEntityType;
    entityId: number;
    affiliateId: number | null;
    reason?: string | null;
}

export async function assignToAffiliate(args: AssignToAffiliateArgs): Promise<void> {
    const base = AFFILIATE_PATH[args.entityType];
    if (!base) {
        throw new Error(`Unknown affiliate-owned entity type: ${args.entityType}`);
    }
    const body: ReassignAffiliateRequest = {
        affiliateId: args.affiliateId,
        reason: args.reason ?? null,
    };
    await api.patch(`${base}/${args.entityId}/affiliate`, body);
}

/** Type guard for callers that route based on a runtime entity type. */
export function isAdvertiserEntityType(t: AssignmentEntityType): t is AdvertiserEntityType {
    return t === "page" || t === "page_draft" || t === "domain" || t === "banner";
}

export function isAffiliateEntityType(t: AssignmentEntityType): t is AffiliateEntityType {
    return t === "campaign" || t === "ad_source";
}

// ─── Filter-param helpers (shared by every list page) ───────────────────────

/**
 * Sentinel value the backend treats as "IS NULL" / "unassigned" for the
 * `advertiserId` and `affiliateId` query params on list endpoints. Mirrors
 * {@code CommerceFilters.UNASSIGNED} on the server.
 */
export const UNASSIGNED_FILTER = -1;

/**
 * Convert the AdvertiserSelect / AffiliateSelect value model into the wire
 * value for `?advertiserId=` / `?affiliateId=`.
 *
 *   undefined → undefined (drop the param — "no filter")
 *   null      → -1         ("IS NULL" — unassigned)
 *   number    → that id    (specific advertiser/affiliate)
 */
export function ownerFilterParam(v: number | null | undefined): number | undefined {
    if (v === undefined) return undefined;
    if (v === null) return UNASSIGNED_FILTER;
    return v;
}

/**
 * Read the AdvertiserSelect value back from a URL search-param string.
 * Inverse of {@link ownerFilterParam} for serialization:
 *
 *   no value / ""   → undefined  ("All")
 *   "-1"            → null       ("Unassigned")
 *   "<n>"           → n          (specific id)
 *   anything else   → undefined  (treat malformed as "All" — fail open)
 */
export function ownerFilterFromUrl(raw: string | null): number | null | undefined {
    if (raw == null || raw === "") return undefined;
    if (raw === String(UNASSIGNED_FILTER)) return null;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : undefined;
}

/** Inverse of {@link ownerFilterFromUrl} — back to a URL-safe string or null to drop. */
export function ownerFilterToUrl(v: number | null | undefined): string | null {
    if (v === undefined) return null;
    if (v === null) return String(UNASSIGNED_FILTER);
    return String(v);
}
